import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

import { X } from "lucide-react";
import { useEmbedRoot } from "~/components/EmbedPortalContext";
import { ICON_STROKE } from "~/lib/icon";

type ToastTone = "info" | "danger" | "success";

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface Toast {
  id: number;
  message: string;
  tone: ToastTone;
  actions?: ToastAction[];
  permanent: boolean;
}

interface ToastTimer {
  endAt: number;
  remaining: number;
  handle: ReturnType<typeof setTimeout> | null;
  hovering: boolean;
  focusing: boolean;
}

interface ToastContextValue {
  toasts: Toast[];
  push: (params: {
    message: string;
    tone?: ToastTone;
    durationMs?: number | null;
    action?: ToastAction;
    actions?: ToastAction[];
  }) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_DURATION_MS = 4_000;

export function useToast(): ToastContextValue {
  const value = useContext(ToastContext);
  if (!value) throw new Error("useToast must be used within a <ToastProvider>");
  return value;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextIdRef = useRef(1);
  const timers = useRef(new Map<number, ToastTimer>());

  const dismiss = useCallback((id: number) => {
    setToasts((list) => list.filter((t) => t.id !== id));
    const entry = timers.current.get(id);
    if (entry?.handle) clearTimeout(entry.handle);
    timers.current.delete(id);
  }, []);

  const applyPause = useCallback(
    (id: number) => {
      const entry = timers.current.get(id);
      if (!entry) return;
      if (entry.hovering || entry.focusing) {
        if (entry.handle) {
          clearTimeout(entry.handle);
          entry.handle = null;
          entry.remaining = Math.max(0, entry.endAt - Date.now());
        }
        return;
      }
      if (entry.handle) return;
      if (entry.remaining <= 0) {
        dismiss(id);
        return;
      }
      entry.endAt = Date.now() + entry.remaining;
      entry.handle = setTimeout(() => dismiss(id), entry.remaining);
    },
    [dismiss],
  );

  const setHovering = useCallback(
    (id: number, hovering: boolean) => {
      const entry = timers.current.get(id);
      if (!entry) return;
      entry.hovering = hovering;
      applyPause(id);
    },
    [applyPause],
  );

  const setFocusing = useCallback(
    (id: number, focusing: boolean) => {
      const entry = timers.current.get(id);
      if (!entry) return;
      entry.focusing = focusing;
      applyPause(id);
    },
    [applyPause],
  );

  const push = useCallback(
    ({
      message,
      tone = "info",
      durationMs,
      action,
      actions,
    }: {
      message: string;
      tone?: ToastTone;
      durationMs?: number | null;
      action?: ToastAction;
      actions?: ToastAction[];
    }) => {
      const id = nextIdRef.current++;
      const merged = [...(action ? [action] : []), ...(actions ?? [])];
      const resolved = merged.length > 0 ? merged : undefined;
      const permanent = durationMs === null;
      setToasts((list) => [...list, { id, message, tone, actions: resolved, permanent }]);
      if (permanent) return;
      const duration = durationMs ?? DEFAULT_DURATION_MS;
      const endAt = Date.now() + duration;
      const handle = setTimeout(() => dismiss(id), duration);
      timers.current.set(id, {
        endAt,
        remaining: duration,
        handle,
        hovering: false,
        focusing: false,
      });
    },
    [dismiss],
  );

  useEffect(() => {
    const active = timers.current;
    return () => {
      for (const entry of active.values()) if (entry.handle) clearTimeout(entry.handle);
      active.clear();
    };
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({ toasts, push, dismiss }),
    [toasts, push, dismiss],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport
        toasts={toasts}
        onDismiss={dismiss}
        onHover={setHovering}
        onFocus={setFocusing}
      />
    </ToastContext.Provider>
  );
}

function ToastViewport({
  toasts,
  onDismiss,
  onHover,
  onFocus,
}: {
  toasts: Toast[];
  onDismiss: (id: number) => void;
  onHover: (id: number, hovering: boolean) => void;
  onFocus: (id: number, focusing: boolean) => void;
}) {
  const embedRoot = useEmbedRoot();
  if (toasts.length === 0) return null;
  const stack = (
    <div
      role="region"
      aria-label="Notifications"
      style={{
        position: embedRoot ? "absolute" : "fixed",
        right: "var(--chat-space-4)",
        bottom: "var(--chat-space-4)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--chat-space-2)",
        zIndex: 1000,
        pointerEvents: "none",
      }}
    >
      {toasts.map((toast) => (
        <ToastCard
          key={toast.id}
          toast={toast}
          onDismiss={() => onDismiss(toast.id)}
          onHover={(hovering) => onHover(toast.id, hovering)}
          onFocus={(focusing) => onFocus(toast.id, focusing)}
        />
      ))}
    </div>
  );
  if (embedRoot) return createPortal(stack, embedRoot);
  return stack;
}

const ACTION_BASE: CSSProperties = {
  background: "transparent",
  border: "none",
  fontFamily: "var(--chat-font-sans)",
  fontSize: "var(--chat-type-body-sm-size)",
  lineHeight: "var(--chat-type-body-sm-line)",
  cursor: "pointer",
  padding: 0,
};

function ToastCard({
  toast,
  onDismiss,
  onHover,
  onFocus,
}: {
  toast: Toast;
  onDismiss: () => void;
  onHover: (hovering: boolean) => void;
  onFocus: (focusing: boolean) => void;
}) {
  const dotColor =
    toast.tone === "danger"
      ? "var(--chat-state-danger)"
      : toast.tone === "success"
        ? "var(--chat-fg-secondary)"
        : "var(--chat-accent)";
  const actions = toast.actions ?? [];
  const hasAction = actions.length > 0;
  const stacked = actions.length >= 2;

  const dot = (
    <span
      aria-hidden
      style={{
        width: 8,
        height: 8,
        borderRadius: "var(--chat-radius-full)",
        background: dotColor,
        flexShrink: 0,
      }}
    />
  );

  const message = (
    <span
      style={{ flex: 1, minWidth: 0, cursor: hasAction ? "pointer" : "inherit" }}
      onClick={hasAction ? onDismiss : undefined}
    >
      {toast.message}
    </span>
  );

  const close = toast.permanent ? (
    <button
      type="button"
      aria-label="Dismiss"
      className="chat-button"
      onClick={(e) => {
        e.stopPropagation();
        onDismiss();
      }}
      style={{
        width: 24,
        height: 24,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: "transparent",
        border: "none",
        color: "var(--chat-fg-secondary)",
        cursor: "pointer",
        padding: 0,
        flexShrink: 0,
      }}
    >
      <X size={16} strokeWidth={ICON_STROKE} />
    </button>
  ) : null;

  const actionButton = (act: ToastAction, i: number) => (
    <button
      key={`${toast.id}-${i}-${act.label}`}
      type="button"
      onClick={() => {
        act.onClick();
        onDismiss();
      }}
      style={
        i === 0
          ? { ...ACTION_BASE, color: "var(--chat-accent)", fontWeight: 500 }
          : { ...ACTION_BASE, color: "var(--chat-fg-secondary)", fontWeight: 400 }
      }
    >
      {act.label}
    </button>
  );

  const cardStyle: CSSProperties = {
    width: 320,
    maxWidth: "calc(100vw - 2 * var(--chat-space-4))",
    display: "flex",
    flexDirection: stacked ? "column" : "row",
    alignItems: stacked ? "stretch" : "center",
    gap: stacked ? 0 : "var(--chat-space-2)",
    padding: "var(--chat-space-3) var(--chat-space-4)",
    borderRadius: "var(--chat-radius-md)",
    background: "var(--chat-surface-overlay)",
    color: "var(--chat-fg-primary)",
    fontFamily: "var(--chat-font-sans)",
    fontSize: "var(--chat-type-body-sm-size)",
    lineHeight: "var(--chat-type-body-sm-line)",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.18)",
    pointerEvents: "auto",
    cursor: hasAction ? "default" : "pointer",
  };

  const signals = {
    onMouseEnter: () => onHover(true),
    onMouseLeave: () => onHover(false),
    onFocus: () => onFocus(true),
    onBlur: () => onFocus(false),
  };

  if (stacked) {
    return (
      <div role="status" aria-live="polite" style={cardStyle} {...signals}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--chat-space-2)",
            width: "100%",
          }}
        >
          {dot}
          {message}
          {close}
        </div>
        <div
          data-toast-actions
          style={{
            display: "flex",
            justifyContent: "flex-end",
            flexWrap: "wrap",
            gap: "var(--chat-space-4)",
            marginTop: "var(--chat-space-2)",
          }}
        >
          {actions.map(actionButton)}
        </div>
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-live="polite"
      onClick={hasAction ? undefined : onDismiss}
      style={cardStyle}
      {...signals}
    >
      {dot}
      {message}
      {close}
      {actions.map(actionButton)}
    </div>
  );
}
